// Main Recording Manager - orchestrates all recording operations
import { doc, getDoc } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { BaseCRUDManager } from './base/BaseCRUDManager';
import { isDemoMode } from '../utilities/demoMode';
import { DemoDataManager } from '../services/demoDataManager';
import { IRecording } from '../types/Recording';
import { IRecordingWithAuthor, ITimingData } from '../types';
import { deriveHasWordLevelTranscriptFromTimingData } from '../utils/recordingTranscription';

// Import modular operations
import { createRecordingWithAuthor, stripRuntimeRecordingFields } from './recording/typeUtils';
import {
  getAllRecordings,
  getRecordingsForPin,
  getRecordingsByProjectKey,
  getRecordingsByAlbumKey,
  getRecordingByAlbumKey,
  getOrphanedRecordingsForProject,
  getRecordingsByAuthorKey,
  updateRecordingsOrder,
  updateEpisodeRecordingsSequence,
  updateRecordingsAuthor,
  fetchRecordingsForEpisodeProject
} from './recording/queries';
import {
  fetchModerationRecordings,
  fetchRecordingsWithoutTranscription,
  updateContentRating,
  removeContentRating,
  getRecordingsByContentRating,
  updateApprovalStatus,
  markRecordingTranscriptionExcluded,
} from './recording/moderation';
import { assignRecordingToPin } from './recording/pinAssignment';
import { assignRecordingAsProjectIntro } from './recording/introAssignment';
import { getSubmissionsByProject, listenToSubmissions } from './recording/submissions';
import { PinManager } from './PinManager';
import { detectStemThresholdMode, buildFixedStemThresholds } from '../utilities/stemThresholdUtils';
import { recordingEventEmitter } from './recording/events';

// Re-export types and event emitter for backward compatibility
export type { ModerationResult, IRecordingWithModerationInfo } from './recording/types';
export { recordingEventEmitter as eventEmitter };

const RECORDINGS_COLLECTION = 'Recordings';

// Method to fetch all recordings from Firestore
export const RecordingManager = {
  // Method to fetch all recordings from Firestore
  observeRecordings: async (): Promise<IRecording[]> => {
    try {
      return await getAllRecordings();
    } catch (error) {
      console.error('Error observing recordings:', error);
      return [];
    }
  },

  // Method to fetch recordings associated with a specific pin key
  getRecordingsForPin: async (pinKey: string): Promise<IRecordingWithAuthor[]> => {
    return getRecordingsForPin(pinKey);
  },

  // Method to fetch a specific recording from Firestore
  getRecording: async (key: string): Promise<IRecording | null> => {
    try {
      return await BaseCRUDManager.getById<IRecording>(
        RECORDINGS_COLLECTION,
        key,
        { idField: 'key' }
      );
    } catch (error) {
      console.error('Error getting recording:', error);
      return null;
    }
  },

  // used in OverhearScreen to collect recording using a pins albumKey
  fetchRecordingByAlbumKey: async (albumKey: string): Promise<IRecording | null> => {
    return getRecordingByAlbumKey(albumKey);
  },

  fetchRecordingsByAlbumKey: async (albumKey: string): Promise<IRecording[]> => {
    return getRecordingsByAlbumKey(albumKey);
  },

  createRecording: async (newRecordingData: IRecordingWithAuthor): Promise<string | null> => {
    try {
      // Use the provided key as the document ID
      await BaseCRUDManager.create<IRecording>(
        RECORDINGS_COLLECTION,
        stripRuntimeRecordingFields(newRecordingData) as any,
        {
          idField: 'key',
          customId: newRecordingData.key,
          autoTimestamps: false // Recording data already has timestamps
        }
      );
      
      return newRecordingData.key;
    } catch (error) {
      console.error('Error creating new recording:', error);
      return null;
    }
  },

  updateRecording: async (key: string, updatedData: Partial<IRecording>): Promise<void> => {
    try {
      await BaseCRUDManager.update<IRecording>(
        RECORDINGS_COLLECTION,
        key,
        stripRuntimeRecordingFields(updatedData),
        { idField: 'key', autoTimestamps: false }
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Error updating recording:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  // Method to update the timingData of a specific recording
  updateTimingData: async (recordingId: string, updatedTimingData: ITimingData[]): Promise<void> => {
    try {
      await BaseCRUDManager.update<IRecording>(
        RECORDINGS_COLLECTION,
        recordingId,
        {
          timingData: updatedTimingData,
          hasWordLevelTranscript: deriveHasWordLevelTranscriptFromTimingData(updatedTimingData),
        },
        { idField: 'key', autoTimestamps: false }
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Error updating timing data:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  removeRecording: async (key: string): Promise<void> => {
    if (isDemoMode()) {
      DemoDataManager.deleteRecording(key);
      return;
    }

    try {
      await BaseCRUDManager.delete(RECORDINGS_COLLECTION, key);
    } catch (error) {
      console.error('Error removing recording:', error);
      throw error;
    }
  },

  getRecordingsByProjectKey: async (project: string, includeDemo: boolean = false): Promise<IRecording[]> => {
    return getRecordingsByProjectKey(project, includeDemo);
  },

  getRecordingWithAuthor: async (key: string): Promise<IRecordingWithAuthor | null> => {
    if (isDemoMode()) {
      try {
        const stored = localStorage.getItem('demo_recordings');
        if (stored) {
          const data = JSON.parse(stored);
          const recording = data[key];
          if (recording) {
            return recording as IRecordingWithAuthor;
          }
        }
      } catch (error) {
        console.error('Error reading demo recording from localStorage:', error);
      }
      return null;
    }

    try {
      const recording = await BaseCRUDManager.getById<IRecording>(
        RECORDINGS_COLLECTION,
        key,
        { idField: 'key' }
      );
      
      if (!recording) {
        return null;
      }

      return await createRecordingWithAuthor(recording as any, key);
    } catch (error) {
      console.error('Error getting recording with author:', error);
      return null;
    }
  },

  getOrphanedRecordingsForProject: async (projectKey: string): Promise<IRecording[]> => {
    return getOrphanedRecordingsForProject(projectKey);
  },

  assignRecordingToPin: async (recordingId: string, pinId: string): Promise<void> => {
    await assignRecordingToPin(recordingId, pinId);
    await RecordingManager.syncCollaborativeStemPinThresholds(pinId);
  },

  assignRecordingAsProjectIntro: async (projectId: string, recordingId: string): Promise<void> => {
    await assignRecordingAsProjectIntro(projectId, recordingId);
  },

  /**
   * Update a collaborative stem pin's locationStemThresholds to match its current
   * number of location stem recordings (sequenceNumber >= 1). Only runs for fixed modes (every 2/3/4/5).
   */
  syncCollaborativeStemPinThresholds: async (pinId: string): Promise<void> => {
    try {
      const pin = await PinManager.getPin(pinId);
      if (!pin || pin.pinType !== 'collaborativeStem') return;
      const recordings = await getRecordingsForPin(pinId);
      const locationStemCount = recordings.filter((r) => (r.sequenceNumber ?? 0) >= 1).length;
      const arr = pin.stemThresholds?.locationStemThresholds ?? [];
      const mode = arr.length === 0 ? 'every2' : detectStemThresholdMode(arr).mode;
      if (mode === 'custom') return;
      const thresholds = buildFixedStemThresholds(mode, locationStemCount);
      await PinManager.updatePin({ ...pin, stemThresholds: { locationStemThresholds: thresholds } });
    } catch (e) {
      console.warn('Could not sync collaborative stem pin thresholds:', e);
    }
  },

  fetchRecordingsForEpisodeProject: async (project_id: string, includeDemo: boolean = true): Promise<IRecordingWithAuthor[]> => {
    return fetchRecordingsForEpisodeProject(project_id, includeDemo);
  },

  updateRecordingsOrder: async (project_id: string, orderedRecordings: { key: string; order: number }[]) => {
    return updateRecordingsOrder(project_id, orderedRecordings);
  },

  updateEpisodeRecordingsSequence: async (
    orderedRecordings: { key: string; sequenceNumber: number }[]
  ) => {
    return updateEpisodeRecordingsSequence(orderedRecordings);
  },

  updateRecordingsAuthor: async (oldAuthorKeys: string[], newAuthorKey: string): Promise<void> => {
    return updateRecordingsAuthor(oldAuthorKeys, newAuthorKey);
  },

  getRecordingsByAuthorKey: async (authorKey: string): Promise<IRecording[]> => {
    return getRecordingsByAuthorKey(authorKey);
  },

  fetchModerationRecordings: async (): Promise<any[]> => {
    return fetchModerationRecordings();
  },

  fetchRecordingsWithoutTranscription: async (): Promise<IRecording[]> => {
    return fetchRecordingsWithoutTranscription();
  },

  updateContentRating: async (
    recordingKey: string, 
    rating: any,
    currentModerationResult?: any
  ): Promise<void> => {
    return updateContentRating(recordingKey, rating, currentModerationResult);
  },

  updateApprovalStatus: async (
    recordingKey: string,
    status: 'pending' | 'approved' | 'rejected',
    approvedBy?: string
  ): Promise<void> => {
    return updateApprovalStatus(recordingKey, status, approvedBy);
  },

  markRecordingTranscriptionExcluded: async (
    recordingKey: string,
    reason: 'non-english'
  ): Promise<void> => {
    return markRecordingTranscriptionExcluded(recordingKey, reason);
  },

  getRecordingsByContentRating: async (rating: any): Promise<any[]> => {
    return getRecordingsByContentRating(rating);
  },

  removeContentRating: async (recordingKey: string): Promise<void> => {
    return removeContentRating(recordingKey);
  },

  getSubmissionsByProject: async (projectId: string): Promise<IRecordingWithAuthor[]> => {
    return getSubmissionsByProject(projectId);
  },

  listenToSubmissions: (
    projectId: string,
    callback: (submissions: IRecordingWithAuthor[]) => void
  ): (() => void) => {
    return listenToSubmissions(projectId, callback);
  }
};
